
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Download, FileText, FileSpreadsheet, Crown, AlertOctagon } from 'lucide-react';
import { Estimate } from '@/types';
import { EstimateStratigraphy } from '@/types/estimateStratigraphy';
import { useToast } from '@/hooks/use-toast';
import {
  exportEstimateStratigraphiesToPDF,
  exportEstimateStratigraphiesToExcel,
} from '@/utils/export';
import { useHasFeature } from '@/hooks/useOrgPlan';
import { useCanWrite } from '@/hooks/useCanWrite';

interface ExportDropdownProps {
  estimate: Estimate;
  stratigraphies?: (EstimateStratigraphy & { stratigraphy?: any })[];
}

const ExportDropdown = ({ estimate, stratigraphies = [] }: ExportDropdownProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);
  const hasExcelExport = useHasFeature('excelExport');
  const { canWrite } = useCanWrite();

  const handleExportPDF = async () => {
    try {
      await exportEstimateStratigraphiesToPDF(estimate, stratigraphies);
      toast({
        title: 'Esportazione PDF completata',
        description: 'Il PDF del preventivo è stato scaricato con successo.',
      });
    } catch (err) {
      console.error('Errore export PDF:', err);
      toast({
        title: 'Errore durante esportazione PDF',
        description: 'Si è verificato un errore durante la generazione del PDF.',
        variant: 'destructive',
      });
    }
  };

  const handleExportExcel = async () => {
    try {
      await exportEstimateStratigraphiesToExcel(estimate, stratigraphies);
      toast({
        title: 'Esportazione Excel completata',
        description: "Il file Excel è stato generato e scaricato.",
      });
    } catch (err) {
      console.error('Errore export Excel:', err);
      toast({
        title: 'Errore durante esportazione Excel',
        description: 'Si è verificato un errore nella generazione del file.',
        variant: 'destructive',
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2" disabled={isExporting}>
          <Download className="h-4 w-4" />
          {isExporting ? 'Generando...' : 'Esporta'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white">
        <DropdownMenuItem onClick={handleExportPDF} className="gap-2">
          <FileText className="h-4 w-4" />
          Esporta PDF
        </DropdownMenuItem>
        {/* Excel: priorità canWrite > hasExcelExport. cancelled → badge rosso "Riattiva". */}
        <DropdownMenuItem
          onClick={() => {
            if (!canWrite || !hasExcelExport) {
              navigate('/settings/billing');
              return;
            }
            handleExportExcel();
          }}
          className={`gap-2 ${(!canWrite || !hasExcelExport) ? 'opacity-70' : ''}`}
        >
          <FileSpreadsheet className="h-4 w-4" />
          <span className="flex-1">Esporta Excel</span>
          {!canWrite ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
              <AlertOctagon className="h-2.5 w-2.5" />
              Riattiva
            </span>
          ) : !hasExcelExport ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
              <Crown className="h-2.5 w-2.5" />
              Studio
            </span>
          ) : null}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportDropdown;
